import { useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers/common";
import { Pagination } from "services/search/types";
import { DataWithPagination } from "services/api/types";
import { initialDataWithPagination } from "services/api/consts";
import { NotificationDefinitionRow, NotificationSearchFormValues } from "services/api/notifications/types";
import { initialNotificationSearchForm } from "services/api/notifications/consts";
import {
  getNotificationDefinitionsAPI,
  deleteNotificationDefinitionAPI,
  toggleNotificationDefinitionActiveAPI,
} from "services/api/notifications/api";
import { Routes } from "../../navigation/routes";
import PageHeader from "ui/components/PageHeader/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import {
  NOTIFICATION_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";
import { Dialog } from "ui/components/Dialog";
import NotificationEditCard from "../NotificationEditCard/NotificationEditCard";
import NotificationSearchFilters from "./components/NotificationSearchFilters/NotificationSearchFilters";
import NotificationSearchResults from "./components/NotificationSearchResults/NotificationSearchResults";
import { NoResults } from "ui/components/NoResults";

const NotificationSearchPage = () => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<NotificationDefinitionRow>
  >(initialDataWithPagination);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [toggleId, setToggleId] = useState<string | null>(null);
  const [toggleIsActive, setToggleIsActive] = useState(false);
  const [openToggleDialog, setOpenToggleDialog] = useState(false);
  const [searchFormValues, setSearchFormValues] = useState<NotificationSearchFormValues>(
    initialNotificationSearchForm
  );

  useEffect(() => {
    fetchResults(searchResults.pagination);
  }, []);

  const fetchResults = async (
    pagination: Pagination,
    filters?: NotificationSearchFormValues | null,
  ) => {
    setIsLoading(true);
    try {
      const result = await getNotificationDefinitionsAPI(pagination, filters);
      setSearchResults(result);
    } catch {
      // error handled by createApiCall
    }
    setIsLoading(false);
  };

  // Filteri se pamte da promjena stranice ne poništi pretragu.
  const handlePaginationChange = (newPagination: Pagination) => {
    fetchResults(newPagination, searchFormValues);
  };

  const handleRowClick = (id: string) => {
    setEditId(id);
    setShowModal(true);
  };

  const handleAddNew = useCallback(() => {
    setEditId(null);
    setShowModal(true);
  }, []);

  const handleEditSuccess = useCallback(() => {
    fetchResults(searchResults.pagination);
    setEditId(null);
    setShowModal(false);
  }, [searchResults.pagination]);

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!deleteId) return;

    try {
      await deleteNotificationDefinitionAPI(deleteId, t);
      await fetchResults(searchResults.pagination);
    } catch {
      // error handled by createApiCall
    }

    setOpenDeleteDialog(false);
    setDeleteId(null);
  }, [deleteId, t, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setDeleteId(null);
    setOpenDeleteDialog(false);
  }, []);

  const handleToggleActive = (id: string, isActive: boolean) => {
    setToggleId(id);
    setToggleIsActive(isActive);
    setOpenToggleDialog(true);
  };

  const handleToggleDialogConfirm = useCallback(async () => {
    if (!toggleId) return;

    try {
      await toggleNotificationDefinitionActiveAPI(toggleId);
      await fetchResults(searchResults.pagination);
    } catch {
      // error handled by createApiCall
    }

    setOpenToggleDialog(false);
    setToggleId(null);
  }, [toggleId, searchResults.pagination]);

  const handleToggleDialogClose = useCallback(() => {
    setToggleId(null);
    setOpenToggleDialog(false);
  }, []);

  const handleSearchChange = useCallback((newSearchFormValues: NotificationSearchFormValues) => {
    setSearchFormValues(newSearchFormValues);
    // Nova pretraga kreće od prve stranice — inače bi na 3. stranici ispalo da nema rezultata.
    fetchResults({ ...searchResults.pagination, pageNumber: 1 }, newSearchFormValues);
  }, [searchResults.pagination]);

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={NOTIFICATION_SEARCH_PAGE_TITLE.text}
              icon={NOTIFICATION_SEARCH_PAGE_TITLE.icon}
              iconColor={NOTIFICATION_SEARCH_PAGE_TITLE.iconColor}
            />
          </PageHeader>
        </div>
        <div className="px-4 ">
          <NotificationSearchFilters
            onSearchChange={handleSearchChange}
            onAddNew={handleAddNew}
          />

          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Unesite notifikaciju klikom na 'Kreiraj notifikaciju'" />
          ) : (
            <NotificationSearchResults
              searchResults={searchResults}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onToggleActive={handleToggleActive}
              onDelete={handleDeleteClick}
            />
          )}
        </div>
      </div>

      <NotificationEditCard
        show={showModal}
        definitionId={editId}
        onSuccess={handleEditSuccess}
        onClose={() => setShowModal(false)}
      />
      <Dialog
        open={openDeleteDialog}
        title={t("admin.notification.dialog.delete.title")}
        message={t("admin.notification.dialog.delete.message")}
        confirmText={t("admin.notification.dialog.delete.confirmtext")}
        cancelText={t("admin.notification.dialog.delete.canceltext")}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
      <Dialog
        open={openToggleDialog}
        title={
          toggleIsActive
            ? t("admin.notification.dialog.deactivate.title")
            : t("admin.notification.dialog.activate.title")
        }
        message={
          toggleIsActive
            ? t("admin.notification.dialog.deactivate.message")
            : t("admin.notification.dialog.activate.message")
        }
        confirmText={
          toggleIsActive
            ? t("admin.notification.dialog.deactivate.confirmtext")
            : t("admin.notification.dialog.activate.confirmtext")
        }
        cancelText={t("admin.notification.dialog.canceltext")}
        onConfirm={handleToggleDialogConfirm}
        onCancel={handleToggleDialogClose}
      />
    </>
  );
};

NotificationSearchPage.route = Routes.NotificationSearchPageRouter;

export default NotificationSearchPage;
